import { Card } from "react-bootstrap";
import { cents, showMoney } from "./Data/Money";
import { Button } from "./Controls/Button";

type Props = {
  title: string;
  total: number;
  variant: "current" | "next";
  onDetails: () => void;
  periodStartDate: string;
  /** When true (current period only), subscription starts after this period — show "Not yet active" language */
  isNotYetActive?: boolean;
};

export function CostProjectionCard({
  title,
  total,
  variant,
  onDetails,
  periodStartDate,
  isNotYetActive = false,
}: Props) {
  const period = getPeriodRange(periodStartDate);
  const hasActivity = total > 0;

  return (
    <Card className={`h-100 ${isNotYetActive ? "bg-light opacity-90" : ""}`}>
      <Card.Body className="d-flex flex-column gap-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold text-uppercase small text-muted">
            {title}
          </div>
        </div>

        {period && (
          <div className="text-muted small">
            {period.start} – {period.end}
          </div>
        )}

        {isNotYetActive && (
          <div className="badge bg-secondary align-self-start">Not yet active</div>
        )}

        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-1 mt-2">
          <div className="small text-muted">
            {isNotYetActive
              ? ""
              : variant === "current"
                ? "Additional Costs"
                : "Projected Cost"}
          </div>

          <div
            className={`fw-bold ${
              variant === "current" ? "text-warning" : "text-danger"
            } fs-3 fs-sm-4`}
          >
            {showMoney(cents(total))}
          </div>
        </div>

        {variant === "current" && !isNotYetActive && (
          <div className="bg-warning-subtle text-warning-emphasis rounded px-3 py-2 small d-flex gap-2 align-items-start mt-2">
            <span className="fw-semibold text-warning">ⓘ</span>
            <span className="lh-sm">
              Activity after the current period starts may incur additional charges
            </span>
          </div>
        )}

        <div className="flex-grow-1" />

        <Button
          variant="outline-secondary"
          size="sm"
          className={`w-100 ${!hasActivity ? "opacity-50 border-secondary" : ""}`}
          disabled={!hasActivity}
          onClick={onDetails}
        >
          Details
        </Button>
      </Card.Body>
    </Card>
  );
}

function getPeriodRange(
  periodStartDate: string,
) {
  const [year, month, day] = periodStartDate.split("-").map(Number);
  const periodStart = new Date(year, month - 1, day);

  let start: Date, end: Date;

  start = new Date(periodStart);
  end = new Date(start);
  end.setMonth(end.getMonth() + 1);
  

  const format = (d: Date) =>
    d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  return {
    start: format(start),
    end: format(end),
  };
}